/**
 * Delivery charges by location and channel.
 *
 * Each row overrides the company default for orders shipping from one location on one
 * channel. POS is not a channel here: a counter sale is carried out, never delivered.
 * An order freezes its charge when it is raised, so editing a row re-prices nothing
 * already placed.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import { QueryState } from '@/components/QueryState'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import {
  DELIVERY_CHANNELS,
  DELIVERY_CHANNEL_LABELS,
  createDeliveryRate,
  deleteDeliveryRate,
  listDeliveryRates,
  updateDeliveryRate,
  type DeliveryChannel,
  type DeliveryRate,
  type DeliveryRateInput,
} from '@/features/company/api'
import { listLocations } from '@/features/orders/api'
import { formatMoney } from '@/lib/money'
import { toast } from '@/lib/toast'

const SELECT =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const AMOUNT = /^\d+(\.\d{1,2})?$/

interface Draft {
  location_id: string
  channel: DeliveryChannel
  flat_rate: string
  free_over: string
}

const EMPTY: Draft = { location_id: '', channel: 'sales_order', flat_rate: '', free_over: '' }

function draftOf(rate: DeliveryRate): Draft {
  return {
    location_id: rate.location_id,
    channel: rate.channel,
    flat_rate: rate.flat_rate,
    free_over: rate.free_over ?? '',
  }
}

/** The problem with a draft, or `null` if it can be sent. */
function problemWith(draft: Draft): string | null {
  if (!draft.location_id) return 'Choose a location.'
  if (!AMOUNT.test(draft.flat_rate.trim())) return 'Enter a charge, or 0 for free delivery.'
  if (draft.free_over.trim() !== '' && !AMOUNT.test(draft.free_over.trim()))
    return 'Free above must be an amount, or empty.'
  return null
}

function inputOf(draft: Draft): DeliveryRateInput {
  return {
    location_id: draft.location_id,
    channel: draft.channel,
    flat_rate: draft.flat_rate.trim(),
    free_over: draft.free_over.trim() === '' ? null : draft.free_over.trim(),
  }
}

export function DeliveryRates({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient()
  // `null`: nothing open. `'new'`: the add form. An id: that row being edited.
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [error, setError] = useState<string | null>(null)

  const rates = useQuery({
    queryKey: ['delivery-rates'],
    queryFn: listDeliveryRates,
    retry: false,
  })
  const locations = useQuery({
    queryKey: ['order-locations'],
    queryFn: listLocations,
    retry: false,
  })
  const locationName = (id: string) =>
    locations.data?.data.find((location) => location.id === id)?.name ?? 'Unknown location'

  const close = () => {
    setEditing(null)
    setDraft(EMPTY)
    setError(null)
  }

  const onFailure = (failure: unknown) =>
    setError(
      failure instanceof ApiError ? failure.message : 'Could not reach the server. Try again.',
    )

  const save = useMutation({
    mutationFn: () =>
      editing && editing !== 'new'
        ? updateDeliveryRate(editing, inputOf(draft))
        : createDeliveryRate(inputOf(draft)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['delivery-rates'] })
      void queryClient.invalidateQueries({ queryKey: ['delivery-quote'] })
      toast.success('Delivery charge saved. New orders use it.')
      close()
    },
    onError: onFailure,
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteDeliveryRate(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['delivery-rates'] })
      void queryClient.invalidateQueries({ queryKey: ['delivery-quote'] })
      toast.success('Removed. That location now charges the default.')
    },
    onError: (failure: unknown) =>
      toast.error(failure instanceof ApiError ? failure.message : 'Could not remove it.'),
  })

  const submit = () => {
    const problem = problemWith(draft)
    if (problem) {
      setError(problem)
      return
    }
    setError(null)
    save.mutate()
  }

  const form = (
    <div className="grid gap-4 rounded-md border border-slate-200 p-4 sm:grid-cols-2 dark:border-slate-800">
      {error && (
        <div className="sm:col-span-2">
          <Alert tone="error">{error}</Alert>
        </div>
      )}
      <Field label="Ships from">
        {(props) => (
          <select
            {...props}
            className={SELECT}
            value={draft.location_id}
            onChange={(event) => setDraft({ ...draft, location_id: event.target.value })}
          >
            <option value="">Choose a location</option>
            {(locations.data?.data ?? []).map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Channel">
        {(props) => (
          <select
            {...props}
            className={SELECT}
            value={draft.channel}
            onChange={(event) =>
              setDraft({ ...draft, channel: event.target.value as DeliveryChannel })
            }
          >
            {DELIVERY_CHANNELS.map((channel) => (
              <option key={channel} value={channel}>
                {DELIVERY_CHANNEL_LABELS[channel]}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Charge" hint="0 delivers free.">
        {(props) => (
          <Input
            {...props}
            inputMode="decimal"
            className="tabular-nums"
            value={draft.flat_rate}
            onChange={(event) => setDraft({ ...draft, flat_rate: event.target.value })}
          />
        )}
      </Field>
      <Field label="Free above" hint="Leave empty to charge on every order.">
        {(props) => (
          <Input
            {...props}
            inputMode="decimal"
            className="tabular-nums"
            placeholder="No threshold"
            value={draft.free_over}
            onChange={(event) => setDraft({ ...draft, free_over: event.target.value })}
          />
        )}
      </Field>
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Button type="button" variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button type="button" loading={save.isPending} onClick={submit}>
          Save charge
        </Button>
      </div>
    </div>
  )

  // The API orders by channel, not by location name -- resolving names is this screen's
  // job, not the company module's (it may not import inventory), so the by-name order a
  // list of locations reads best in is applied here instead.
  const rows = [...(rates.data ?? [])].sort((a, b) =>
    locationName(a.location_id).localeCompare(locationName(b.location_id)),
  )

  return (
    <Card>
      <CardHeader
        title="Delivery charges by location"
        description="Overrides the default for orders shipping from a location. Online orders are charged automatically; on a sales order it is a suggestion the rep can change. POS never charges delivery."
      />
      <CardBody className="space-y-4">
        <QueryState isLoading={rates.isLoading} error={rates.error}>
          {rows.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No location has its own charge. Every order pays the default.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800">
                    <th className="py-2 pr-3 font-medium">Ships from</th>
                    <th className="py-2 pr-3 font-medium">Channel</th>
                    <th className="py-2 pr-3 text-right font-medium">Charge</th>
                    <th className="py-2 pr-3 text-right font-medium">Free above</th>
                    {canEdit && <th className="py-2" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {rows.map((rate) => (
                    <tr key={rate.id}>
                      <td className="py-2 pr-3">{locationName(rate.location_id)}</td>
                      <td className="py-2 pr-3">{DELIVERY_CHANNEL_LABELS[rate.channel]}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {Number(rate.flat_rate) === 0 ? 'Free' : formatMoney(rate.flat_rate)}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {rate.free_over === null ? 'Never' : formatMoney(rate.free_over)}
                      </td>
                      {canEdit && (
                        <td className="py-2 text-right whitespace-nowrap">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setEditing(rate.id)
                              setDraft(draftOf(rate))
                              setError(null)
                            }}
                          >
                            Edit
                          </Button>{' '}
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            loading={remove.isPending && remove.variables === rate.id}
                            onClick={() => remove.mutate(rate.id)}
                            aria-label={`Remove ${locationName(rate.location_id)} ${DELIVERY_CHANNEL_LABELS[rate.channel]} charge`}
                          >
                            Remove
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </QueryState>

        {canEdit &&
          (editing ? (
            form
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setEditing('new')
                setDraft(EMPTY)
                setError(null)
              }}
            >
              Add a location charge
            </Button>
          ))}
      </CardBody>
    </Card>
  )
}
